import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, MapPin, Stethoscope, Loader2, ChevronRight } from "lucide-react";

const LEVEL_LABEL = {
  1: "Direct recruits", 2: "Level 2", 3: "Level 3", 4: "Level 4", 5: "Level 5", 6: "Level 6",
};

export default function Team() {
  const [levels, setLevels] = useState(null);

  useEffect(() => {
    api.get("/team").then(({ data }) => setLevels(data.levels)).catch(() => setLevels({}));
  }, []);

  if (!levels) return <div className="flex items-center justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-navy" /></div>;

  const total = Object.values(levels).reduce((a, b) => a + b.length, 0);

  return (
    <div data-testid="team-page">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-patriot font-bold">Network</p>
        <h1 className="font-display text-3xl text-navy mt-1">My Team</h1>
        <p className="text-muted-foreground mt-2">Your six-level downline — {total} representative{total !== 1 ? "s" : ""} total.</p>
      </div>

      {total === 0 ? (
        <div className="bg-white border border-dashed border-border rounded-xl p-16 text-center" data-testid="team-empty">
          <Users className="h-12 w-12 text-navy/20 mx-auto mb-4" />
          <h3 className="font-display text-xl text-navy">Your team starts here</h3>
          <p className="text-muted-foreground mt-2 max-w-sm mx-auto">Share your referral link to recruit representatives and build your six-level network.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {[1, 2, 3, 4, 5, 6].map((lvl) => {
            const members = levels[lvl] || [];
            if (members.length === 0) return null;
            return (
              <div key={lvl} data-testid={`team-level-${lvl}`}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="h-8 w-8 rounded-lg bg-navy text-white flex items-center justify-center font-mono text-sm font-bold">{lvl}</span>
                  <h2 className="font-display text-xl text-navy">{LEVEL_LABEL[lvl]}</h2>
                  <span className="text-sm text-muted-foreground">({members.length})</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" style={{ marginLeft: `${(lvl - 1) * 12}px` }}>
                  {members.map((m) => (
                    <div key={m.id} className="bg-white border border-border rounded-xl p-5 flex items-center gap-4">
                      <div className="h-11 w-11 rounded-lg bg-navy/5 flex items-center justify-center font-display font-bold text-navy shrink-0">
                        {(m.name || "R").split(" ").map((n) => n[0]).join("").slice(0, 2)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-navy truncate">{m.name}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          {(m.city || m.state) && (<><MapPin className="h-3 w-3" /> {[m.city, m.state].filter(Boolean).join(", ")}</>)}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="flex items-center gap-1 text-sm font-semibold text-navy"><Stethoscope className="h-3.5 w-3.5" /> {m.physicians}</p>
                        <p className="text-[10px] text-muted-foreground uppercase">onboarded</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
